import type { JudgeBallotProject, JudgingCriterion } from '@/types';

// 0 to 5, at most one decimal, and only a dot as the decimal point.
const SCORE_FORMAT = /^\d(?:\.\d)?$/;

export function parseScore(value: string): number | null {
  if (!SCORE_FORMAT.test(value.trim())) return null;
  const score = Number(value.trim());
  return score >= 0 && score <= 5 ? score : null;
}

// Why a typed grade is not valid, or null when it is (or the field is empty). Short on
// purpose: it sits in a one-line slot under the field, so showing it moves nothing.
export function scoreError(value: string): string | null {
  const text = value.trim();
  if (text === '' || parseScore(text) !== null) return null;
  if (/^\d+,\d*$/.test(text)) return 'Usa punto: 4.5';
  if (/^\d+\.\d{2,}$/.test(text)) return 'Máx. 1 decimal';
  return 'Entre 0 y 5';
}

export function formatScore(score: number | undefined): string {
  return score === undefined ? '' : String(score);
}

// The 0–5 weighted grade of a project, or null until it has a grade on every criterion.
export function weightedScore(
  scores: Record<string, number>,
  criteria: JudgingCriterion[],
): number | null {
  if (criteria.length === 0) return null;
  if (criteria.some((criterion) => scores[criterion.id] === undefined)) return null;

  return criteria.reduce(
    (sum, criterion) => sum + (scores[criterion.id] * criterion.weight) / 100,
    0,
  );
}

export const NO_TRACK = 'Sin track';

export function trackOf(project: JudgeBallotProject): string {
  return project.trackName ?? NO_TRACK;
}

// Within a hundredth they are tied: that is the precision the judge sees.
const TIE_EPSILON = 0.005;

// The leaders of a group by weighted grade — more than one when they are tied.
export function leaders(
  projects: JudgeBallotProject[],
  criteria: JudgingCriterion[],
): JudgeBallotProject[] {
  const scored = projects
    .map((project) => ({ project, score: weightedScore(project.scores, criteria) }))
    .filter((entry): entry is { project: JudgeBallotProject; score: number } =>
      entry.score !== null,
    );
  if (scored.length === 0) return [];

  const best = Math.max(...scored.map((entry) => entry.score));
  return scored
    .filter((entry) => best - entry.score < TIE_EPSILON)
    .map((entry) => entry.project);
}

// Each project's place in a group by weighted grade, 1 for the best. Tied projects
// share a place (1, 1, 3); one without a full grade has none.
export function ranks(
  projects: JudgeBallotProject[],
  criteria: JudgingCriterion[],
): Map<string, number | null> {
  const scores = projects.map((project) => weightedScore(project.scores, criteria));
  return new Map(
    projects.map((project, index) => {
      const score = scores[index];
      if (score === null) return [project.id, null];
      const ahead = scores.filter(
        (other) => other !== null && other - score >= TIE_EPSILON,
      ).length;
      return [project.id, ahead + 1];
    }),
  );
}

export type Standings = {
  overall: JudgeBallotProject[];
  // Track name -> its leaders, tracks in name order.
  byTrack: [string, JudgeBallotProject[]][];
};

export function standings(
  projects: JudgeBallotProject[],
  criteria: JudgingCriterion[],
): Standings {
  const tracks = [...new Set(projects.map(trackOf))].sort((a, b) =>
    a.localeCompare(b),
  );
  return {
    overall: leaders(projects, criteria),
    byTrack: tracks.map((track) => [
      track,
      leaders(
        projects.filter((project) => trackOf(project) === track),
        criteria,
      ),
    ]),
  };
}
